import React from "react"

import { useState, useEffect } from "react"
import Link from "@/src/components/Link"
import { Eye, EyeOff, Phone } from "lucide-react"
import { AuthLayout } from "@/components/auth/auth-layout"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Checkbox } from "@/components/ui/checkbox"
import { Button } from "@/components/ui/button"
// import { SocialLoginButton } from "@/components/auth/social-login-button"
import { useRouter } from "@/src/hooks/useRouter"
import { useAuth } from "@/src/contexts/AuthContext"

export default function LoginPage() {
  const [identifier, setIdentifier] = useState("") // Email or Mobile
  const [password, setPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const router = useRouter()
  const { login: authLogin } = useAuth()

  // Validate if identifier is either valid email or valid mobile
  const isEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(identifier)
  const isMobile = /^0[6-9]\d{8}$/.test(identifier)
  const isIdentifierValid = isEmail || isMobile

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setError("")
    
    if (!identifier || !password) {
      setError("กรุณากรอกอีเมลหรือเบอร์โทรศัพท์ และรหัสผ่าน")
      return
    }

    if (!isIdentifierValid) {
      setError("รูปแบบอีเมลหรือเบอร์โทรศัพท์ไม่ถูกต้อง")
      return
    }

    try {
      setLoading(true)
      
      // Use AuthContext login
      await authLogin(identifier, password)
      
      // Handle remember me functionality
      if (rememberMe) {
        localStorage.setItem('rememberLogin', 'true')
        localStorage.setItem('rememberedIdentifier', identifier)
      } else {
        localStorage.removeItem('rememberLogin')
        localStorage.removeItem('rememberedIdentifier')
      }
      
      // Hard-lock policy: only fully approved KYC can go to dashboard
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      if (user.kycVerified === true) {
        router.push("/dashboard")
      } else {
        router.push("/auth/verify")
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || "เข้าสู่ระบบไม่สำเร็จ")
    } finally {
      setLoading(false)
    }
  }

  // Load remembered identifier on component mount
  useEffect(() => {
    const remembered = localStorage.getItem('rememberLogin')
    if (remembered === 'true') {
      const rememberedIdentifier = localStorage.getItem('rememberedIdentifier')
      if (rememberedIdentifier) {
        setIdentifier(rememberedIdentifier)
        setRememberMe(true)
      }
    }
  }, [])

  return (
    <AuthLayout>
      <div className="bg-[#e4ffe5] rounded-2xl shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-[#323130] mb-2">เข้าสู่ระบบ</h1>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          {/* Email or Mobile Field */}
          <div className="space-y-2">
            <Label htmlFor="identifier" className="text-[#323130] font-medium">
              อีเมลหรือเบอร์โทรศัพท์
            </Label>
            <div className="relative">
              <Input
                id="identifier"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="กรอกอีเมลหรือหมายเลขโทรศัพท์"
                className="bg-white border-[#d1d1d1] text-[#323130] pr-10"
              />
              {isIdentifierValid && (
                <Phone className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-green-600" />
              )}
            </div>
            {identifier && !isIdentifierValid && (
              <p className="text-xs text-orange-600">
                กรุณากรอกอีเมล (เช่น user@example.com) หรือเบอร์โทรศัพท์ (10 หลัก)
              </p>
            )}
          </div>

          {/* Password Field */}
          <div className="space-y-2">
            <Label htmlFor="password" className="text-[#323130] font-medium">
              รหัสผ่าน
            </Label>
            <div className="relative">
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="กรอกรหัสผ่าน"
                className="bg-white border-[#d1d1d1] text-[#323130] pr-10"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#949494] hover:text-[#323130] transition-colors"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="text-red-600 text-sm text-center">
              {error}
            </div>
          )}

          {/* Remember Me & Forgot Password */}
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Checkbox
                id="remember"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked as boolean)}
              />
              <Label htmlFor="remember" className="text-[#323130] text-sm cursor-pointer">
                จดจำการเข้าสู่ระบบ
              </Label>
            </div>
            <Link href="/auth/forgot-password" className="text-[#323130] text-sm hover:underline transition-colors">
              ลืมรหัสผ่าน
            </Link>
          </div>

          {/* Login Button */}
          <Button 
            type="submit" 
            disabled={loading || !isIdentifierValid || !password}
            className="w-full bg-brand-primary text-white font-medium py-3 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "กำลังเข้าสู่ระบบ..." : "เข้าสู่ระบบ"}
          </Button>
        </form>

        {/* <div className="mt-6 grid grid-cols-3 gap-2">
          <SocialLoginButton provider="google" disabled>
          </SocialLoginButton>
          <SocialLoginButton provider="facebook" disabled>
          </SocialLoginButton>
          <SocialLoginButton provider="line" disabled>
          </SocialLoginButton>
        </div> */}

        <div className="mt-8 text-center">
          <p className="text-brand-text-base">
            ยังไม่เป็นสมาชิก?{" "}
            <Link href="/auth/register" className="text-brand-red hover:underline font-medium">
              <span className="text-brand-red">สมัครสมาชิก</span>
            </Link>
          </p>
        </div>
      </div>
    </AuthLayout>
  )
}
